import React from "react";
import { NavLink } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";

const Footer = () => {
  const { t, lang } = useI18n();
  const base = `/${lang}`;

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-[#0b224a] bg-[#0F2C59] text-white/90"
    >
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 lg:grid-cols-4 lg:px-8">
        <div className="lg:col-span-1">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-[#D4AF37]/60 bg-[#0b224a] font-serif text-lg font-semibold text-[#D4AF37]"
            >
              K
            </span>
            <span className="font-serif text-lg font-semibold text-white">
              {t("footer.brand")}
            </span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-white/70">
            {t("footer.description")}
          </p>
        </div>

        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37]">
            {t("footer.columns.navigation.title")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <NavLink
                to={base}
                end
                className="text-white/80 hover:text-white"
                data-testid="footer-link-home"
              >
                {t("footer.columns.navigation.home")}
              </NavLink>
            </li>
            <li>
              <a
                href={`${base}#services`}
                className="text-white/80 hover:text-white"
                data-testid="footer-link-services"
              >
                {t("footer.columns.navigation.services")}
              </a>
            </li>
            <li>
              <a
                href={`${base}#resources`}
                className="text-white/80 hover:text-white"
                data-testid="footer-link-resources"
              >
                {t("footer.columns.navigation.resources")}
              </a>
            </li>
            <li>
              <a
                href={`${base}#contact`}
                className="text-white/80 hover:text-white"
                data-testid="footer-link-contact"
              >
                {t("footer.columns.navigation.contact")}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37]">
            {t("footer.columns.legal.title")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <NavLink
                to={`${base}/privacy`}
                className="text-white/80 hover:text-white"
                data-testid="footer-link-privacy"
              >
                {t("footer.columns.legal.privacy")}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={`${base}/cookies`}
                className="text-white/80 hover:text-white"
                data-testid="footer-link-cookies"
              >
                {t("footer.columns.legal.cookies")}
              </NavLink>
            </li>
            <li>
              <NavLink
                to={`${base}/terms`}
                className="text-white/80 hover:text-white"
                data-testid="footer-link-terms"
              >
                {t("footer.columns.legal.terms")}
              </NavLink>
            </li>
          </ul>
        </div>

        <div id="contact">
          <h4 className="text-sm font-semibold uppercase tracking-wider text-[#D4AF37]">
            {t("footer.columns.contact.title")}
          </h4>
          <ul className="mt-4 space-y-2 text-sm text-white/80">
            <li>
              <span className="block text-xs uppercase tracking-wide text-white/50">
                {t("footer.columns.contact.addressLabel")}
              </span>
              <span>{t("footer.columns.contact.address")}</span>
            </li>
            <li>
              <span className="block text-xs uppercase tracking-wide text-white/50">
                {t("footer.columns.contact.phoneLabel")}
              </span>
              <a
                href={`tel:${t("footer.columns.contact.phone").replace(/\s+/g, "")}`}
                className="hover:text-white"
                data-testid="footer-phone"
              >
                {t("footer.columns.contact.phone")}
              </a>
            </li>
            <li>
              <span className="block text-xs uppercase tracking-wide text-white/50">
                {t("footer.columns.contact.emailLabel")}
              </span>
              <a
                href={`mailto:${t("footer.columns.contact.email")}`}
                className="hover:text-white"
                data-testid="footer-email"
              >
                {t("footer.columns.contact.email")}
              </a>
            </li>
            <li className="text-white/60">
              {t("footer.columns.contact.hours")}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 bg-[#0b224a]">
        <div className="mx-auto max-w-6xl px-5 py-6 text-xs text-white/70 lg:px-8">
          <div className="grid gap-2 md:grid-cols-2">
            <div className="space-y-1">
              <p className="font-semibold text-white">
                {t("footer.legalDetails.company")}
              </p>
              <p>{t("footer.legalDetails.rfc")}</p>
              <p>{t("footer.legalDetails.cnbv")}</p>
              <p>{t("footer.legalDetails.curp")}</p>
              <p>{t("footer.legalDetails.jurisdiction")}</p>
            </div>
            <div className="space-y-2 md:text-right">
              <p className="text-white/60">
                {t("footer.legalDetails.disclaimer")}
              </p>
              <p className="text-white/50">{t("footer.copyright")}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
